<template>
  <div class="device-rentals-page">
    <header-portrate />
    <TabsComp :tabs="tabs" />

    <div class="rentals-shell" :style="`direction:${lang.dir || 'ltr'}`">
      <div class="rentals-toolbar">
        <div>
          <h5 class="page-title">{{ t('device_rental', 'Device Rental') }}</h5>
          <div class="muted-line">{{ t('items', 'Items') }}: {{ rentals.length }}</div>
        </div>
        <div class="toolbar-actions">
          <b-form-input v-model="filters.search" class="filter-input" :placeholder="t('search', 'Search')" @keyup.enter="loadRentals" />
          <b-form-select v-model="filters.status" :options="statusOptions" class="filter-select" @change="loadRentals" />
          <b-button size="sm" variant="primary" @click="loadRentals" :disabled="loading">
            <v-icon small class="mr-1">mdi-refresh</v-icon>
            {{ t('refresh', 'Refresh') }}
          </b-button>
          <b-button size="sm" variant="success" v-b-toggle.device_rental_sidebar @click="openCreate">
            <v-icon small class="mr-1">mdi-plus</v-icon>
            {{ t('new', 'New') }}
          </b-button>
        </div>
      </div>

      <div class="summary-grid">
        <button class="summary-cell current" @click="setStatus('current')">
          <span>{{ t('current', 'Current') }}</span>
          <strong>{{ summary.current || 0 }}</strong>
        </button>
        <button class="summary-cell" @click="setStatus('new')">
          <span>{{ t('new', 'New') }}</span>
          <strong>{{ summary.new || 0 }}</strong>
        </button>
        <button class="summary-cell active" @click="setStatus('active')">
          <span>{{ t('active', 'Active') }}</span>
          <strong>{{ summary.active || 0 }}</strong>
        </button>
        <button class="summary-cell overdue" @click="setStatus('overdue')">
          <span>{{ t('overdue', 'Overdue') }}</span>
          <strong>{{ summary.overdue || 0 }}</strong>
        </button>
        <button class="summary-cell returned" @click="setStatus('returned')">
          <span>{{ t('returned', 'Returned') }}</span>
          <strong>{{ summary.returned || 0 }}</strong>
        </button>
        <button class="summary-cell invoiced" @click="setStatus('invoiced')">
          <span>{{ t('invoiced', 'Invoiced') }}</span>
          <strong>{{ summary.invoiced || 0 }}</strong>
        </button>
      </div>

      <v-simple-table class="rentals-table">
        <thead>
          <tr>
            <th class="text-center backBlack">{{ t('number', '#') }}</th>
            <th class="text-center backBlack">{{ t('customer', 'Customer') }}</th>
            <th class="text-center backBlack">{{ t('mobile', 'Mobile') }}</th>
            <th class="text-center backBlack">{{ t('devices', 'Devices') }}</th>
            <th class="text-center backBlack">{{ t('start', 'Start') }}</th>
            <th class="text-center backBlack">{{ t('deadline', 'Deadline') }}</th>
            <th class="text-center backBlack">{{ t('total', 'Total') }}</th>
            <th class="text-center backBlack">{{ t('remaining', 'Remaining') }}</th>
            <th class="text-center backBlack">{{ t('status', 'Status') }}</th>
            <th class="text-center backBlack">{{ t('action', 'Action') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="rental in rentals" :key="rental.id" :class="{ overdue: rental.is_overdue }">
            <td>#{{ rental.rental_number || rental.id }}</td>
            <td>{{ rental.customer_name || '-' }}</td>
            <td>{{ rental.mobile || '-' }}</td>
            <td>
              <span v-for="item in rental.items" :key="item.id" class="device-chip">{{ item.device_name }}</span>
            </td>
            <td>{{ formatDate(rental.start_date) }}</td>
            <td>{{ formatDate(rental.expected_return_date) }}</td>
            <td>{{ money(displayTotal(rental)) }}</td>
            <td>{{ money(rental.remaining_amount) }}</td>
            <td>
              <span class="status-pill" :style="{ backgroundColor: rental._status_bg, color: rental._status_color }">
                {{ rental[`_status_${lang.langname}`] || rental._status_en }}
              </span>
            </td>
            <td>
              <b-button size="sm" variant="outline-primary" @click="viewRental(rental)">
                <v-icon small>mdi-eye-outline</v-icon>
              </b-button>
            </td>
          </tr>
          <tr v-if="!rentals.length">
            <td colspan="10" class="empty-state">{{ t('no_data', 'No data') }}</td>
          </tr>
        </tbody>
      </v-simple-table>
    </div>

    <b-sidebar id="device_rental_sidebar" no-header backdrop width="95%" right shadow>
      <template #default="{ hide }">
        <div class="sidebar-head">
          <strong>{{ t('device_rental', 'Device Rental') }}</strong>
          <b-button size="sm" variant="danger" @click="hide">{{ t('close', 'Close') }}</b-button>
        </div>
        <div class="rental-form" :style="`direction:${lang.dir || 'ltr'}`">
          <section class="form-section">
            <div class="section-title">{{ t('customer_information', 'Customer Information') }}</div>
            <div class="customer-type-bar">
              <span>{{ t('customer_type', 'Customer Type') }}</span>
              <button
                type="button"
                class="customer-type-button"
                :class="{ selected: Number(form.customer_type) === 1 }"
                @click="changeRentalCustomerType(1)"
              >
                {{ t('individual_client', 'Individual') }}
                <i v-if="Number(form.customer_type) === 1" class="fas fa-arrow-down"></i>
              </button>
              <button
                type="button"
                class="customer-type-button"
                :class="{ selected: Number(form.customer_type) === 2 }"
                @click="changeRentalCustomerType(2)"
              >
                {{ t('company_clinet', 'Company') }}
                <i v-if="Number(form.customer_type) === 2" class="fas fa-arrow-down"></i>
              </button>
              <button
                type="button"
                class="customer-type-button"
                :class="{ selected: Number(form.customer_type) === 3 }"
                @click="changeRentalCustomerType(3)"
              >
                {{ t('government_clinet', 'Government') }}
                <i v-if="Number(form.customer_type) === 3" class="fas fa-arrow-down"></i>
              </button>
            </div>

            <v-row v-if="Number(form.customer_type) === 1">
              <v-col cols="12" md="2" sm="4" xs="12" v-if="$store.state.licenseType.showIDNumber">
                <label>{{ t('customerid', 'Customer ID') }}</label>
                <b-form-input v-model="form.customer_id" class="inborder" @change="lookupRentalCustomer('id')" />
              </v-col>
              <v-col cols="12" md="2" sm="4" xs="12">
                <label>{{ t('mobile', 'Mobile') }}</label>
                <b-form-input v-model="form.mobile" class="inborder" @change="lookupRentalCustomer('mobile')" />
              </v-col>
              <v-col cols="12" md="2" sm="4" xs="12" v-if="$store.state.licenseType.showIDNumber">
                <label>{{ t('id_number', 'ID Number') }}</label>
                <b-form-input v-model="form.id_number" class="inborder" />
              </v-col>
              <v-col cols="12" md="3" sm="8" xs="12">
                <label>{{ t('customer_name', 'Customer Name') }}</label>
                <b-form-input v-model="form.customer_name" class="inborder customer-main-field" />
              </v-col>
              <v-col cols="12" md="3" sm="12" xs="12">
                <label>{{ t('notes', 'Notes') }}</label>
                <b-form-input v-model="form.notes" class="inborder" />
              </v-col>
            </v-row>

            <v-row v-if="Number(form.customer_type) === 2">
              <v-col cols="12" md="2" sm="4" xs="12" v-if="$store.state.licenseType.showIDNumber">
                <label>{{ t('customerid', 'Customer ID') }}</label>
                <b-form-input v-model="form.customer_id" class="inborder" @change="lookupRentalCustomer('id')" />
              </v-col>
              <v-col cols="12" md="2" sm="4" xs="12">
                <label>{{ t('mobile', 'Mobile') }}</label>
                <b-form-input v-model="form.mobile" class="inborder" @change="lookupRentalCustomer('mobile')" />
              </v-col>
              <v-col cols="12" md="3" sm="12" xs="12">
                <label>{{ t('company_name', 'Company Name') }}</label>
                <b-form-input v-model="form.company.fullname" class="inborder customer-main-field" @input="syncRentalCustomerName" />
              </v-col>
              <v-col cols="12" md="2" sm="12" xs="12">
                <label>{{ t('company_vatid', 'VAT ID') }}</label>
                <b-form-input v-model="form.company.vatid" class="inborder" />
              </v-col>
              <v-col cols="12" md="1" sm="12" xs="12">
                <label>{{ t('building_no', 'Building') }}</label>
                <b-form-input v-model="form.company.building_no" class="inborder address-field" />
              </v-col>
              <v-col cols="12" md="2" sm="12" xs="12">
                <label>{{ t('crt_number', 'CR Number') }}</label>
                <b-form-input v-model="form.company.crt_number" class="inborder address-field" />
              </v-col>
              <v-col cols="12" md="2" sm="12" xs="12">
                <label>{{ t('street_name', 'Street') }}</label>
                <b-form-input v-model="form.company.street_name" class="inborder address-field" />
              </v-col>
              <v-col cols="12" md="1" sm="12" xs="12">
                <label>{{ t('branch_number', 'Branch No.') }}</label>
                <b-form-input v-model="form.company.branch_number" class="inborder address-field" />
              </v-col>
              <v-col cols="12" md="1" sm="12" xs="12">
                <label>{{ t('zipcode', 'Zip') }}</label>
                <b-form-input v-model="form.company.zipcode" class="inborder address-field" />
              </v-col>
              <v-col cols="12" md="2" sm="12" xs="12">
                <label>{{ t('district', 'District') }}</label>
                <b-form-input v-model="form.company.district" class="inborder address-field" />
              </v-col>
              <v-col cols="12" md="1" sm="12" xs="12">
                <label>{{ t('city', 'City') }}</label>
                <b-form-input v-model="form.company.city" class="inborder address-field" />
              </v-col>
              <v-col cols="12" md="3" sm="12" xs="12">
                <label>{{ t('comapny_address', 'Company Address') }}</label>
                <b-form-input v-model="form.company.address" class="inborder customer-main-field" />
              </v-col>
              <v-col cols="12" md="2" sm="12" xs="12">
                <label>{{ t('po_number', 'PO Number') }}</label>
                <b-form-input v-model="form.company.po_number" class="inborder" />
              </v-col>
              <v-col cols="12" md="3" sm="12" xs="12">
                <label>{{ t('notes', 'Notes') }}</label>
                <b-form-input v-model="form.notes" class="inborder" />
              </v-col>
            </v-row>

            <v-row v-if="Number(form.customer_type) === 3">
              <v-col cols="12" md="2" sm="4" xs="12">
                <label>{{ t('mobile', 'Mobile') }}</label>
                <b-form-input v-model="form.mobile" class="inborder" @change="lookupRentalCustomer('mobile')" />
              </v-col>
              <v-col cols="12" md="3" sm="12" xs="12">
                <label>{{ t('entity_name', 'Entity Name') }}</label>
                <b-form-input v-model="form.company.entity_name" class="inborder customer-main-field" @input="syncRentalCustomerName" />
              </v-col>
              <v-col cols="12" md="3" sm="12" xs="12">
                <label>{{ t('branch_name', 'Branch Name') }}</label>
                <b-form-input v-model="form.company.branch_name" class="inborder" />
              </v-col>
              <v-col cols="12" md="2" sm="12" xs="12">
                <label>{{ t('request_number', 'Request Number') }}</label>
                <b-form-input v-model="form.company.request_number" class="inborder" />
              </v-col>
              <v-col cols="12" md="2" sm="12" xs="12">
                <label>{{ t('order_number', 'Order Number') }}</label>
                <b-form-input v-model="form.company.order_number" class="inborder" />
              </v-col>
              <v-col cols="12" md="2" sm="12" xs="12">
                <label>{{ t('po_number', 'PO Number') }}</label>
                <b-form-input v-model="form.company.po_number" class="inborder" />
              </v-col>
              <v-col cols="12" md="4" sm="12" xs="12">
                <label>{{ t('notes', 'Notes') }}</label>
                <b-form-input v-model="form.notes" class="inborder" />
              </v-col>
            </v-row>
          </section>

          <section class="form-section">
            <div class="section-row">
              <div class="section-title">{{ t('devices', 'Devices') }}</div>
              <div class="device-search">
                <b-form-input v-model="deviceSearch" size="sm" :placeholder="t('search', 'Search')" @input="onDeviceSearchInput" @keyup.enter="loadDevices" />
                <b-button size="sm" variant="outline-primary" @click="loadDevices">{{ t('search', 'Search') }}</b-button>
                <b-button size="sm" variant="primary" @click="addItem">{{ t('add', 'Add') }}</b-button>
              </div>
            </div>
            <div v-if="deviceSearchDone && devices.length" class="device-results">
              <div class="device-results-head">
                <span>{{ t('item_code', 'Item code') }}</span>
                <span>{{ t('device_name', 'Device name') }}</span>
                <span>{{ t('unit_price', 'Unit Price') }}</span>
                <span></span>
              </div>
              <div v-for="device in devices" :key="device.id" class="device-result-row">
                <span class="device-result-code">{{ deviceCode(device) || '-' }}</span>
                <span class="device-result-name">{{ deviceName(device) }}</span>
                <span>{{ money(device.default_price) }}</span>
                <b-button size="sm" variant="outline-primary" @click="addDeviceToRental(device)">{{ t('add', 'Add') }}</b-button>
              </div>
            </div>
            <div v-else-if="deviceSearchDone" class="device-results-empty">
              {{ t('no_data', 'No data') }}
            </div>
            <v-simple-table class="items-table">
              <thead>
                <tr>
                  <th class="text-center backBlack">{{ t('device', 'Device') }}</th>
                  <th class="text-center backBlack">{{ t('period', 'Period') }}</th>
                  <th class="text-center backBlack">{{ t('unit_price', 'Unit Price') }}</th>
                  <th class="text-center backBlack">{{ t('quantity', 'Quantity') }}</th>
                  <th class="text-center backBlack">{{ t('units', 'Units') }}</th>
                  <th class="text-center backBlack">{{ t('total', 'Total') }}</th>
                  <th class="text-center backBlack">{{ t('notes', 'Notes') }}</th>
                  <th class="text-center backBlack"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in form.items" :key="index">
                  <td class="device-select-cell">
                    <b-form-select class="selborder" v-model="item.device_id" :options="deviceOptions" @change="selectDevice(index)" />
                    <b-form-input v-model="item.device_name" size="sm" :placeholder="t('device_name', 'Device name')" />
                  </td>
                  <td><b-form-select  class="selborder" v-model="item.period_type" :options="periodOptions" @change="calcItem(item)" /></td>
                  <td><b-form-input v-model="item.unit_price" type="number" min="0" step="0.01" @input="calcItem(item)" /></td>
                  <td><b-form-input v-model="item.quantity" type="number" min="1" step="1" @input="calcItem(item)" /></td>
                  <td><b-form-input v-model="item.planned_units" type="number" min="1" step="1" @input="calcItem(item)" /></td>
                  <td>{{ money(item.planned_total) }}</td>
                  <td><b-form-input v-model="item.item_notes" /></td>
                  <td>
                    <b-button size="sm" variant="outline-danger" @click="removeItem(index)">
                      <v-icon small>mdi-delete-outline</v-icon>
                    </b-button>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </section>

          <section class="form-section">
            <div class="section-title">{{ t('payment', 'Payment') }}</div>
            <v-row>
              <v-col cols="12" md="3">
                <label>{{ t('advanced_payment', 'Advanced Payment') }}</label>
                <b-form-input v-model="form.advance_amount" type="number" min="0" step="0.01" />
              </v-col>
              <v-col cols="12" md="3">
                <label>{{ t('payment_method', 'Payment Method') }}</label>
                <b-form-select  class="selborder" v-model="form.advance_paytype" :options="paytypeOptions" />
              </v-col>
              <v-col cols="12" md="3">
                <label>{{ t('insurance_amount', 'Insurance Amount') }}</label>
                <b-form-input v-model="form.insurance_amount" type="number" min="0" step="0.01" />
              </v-col>
              <v-col cols="12" md="3">
                <label>{{ t('payment_method', 'Payment Method') }}</label>
                <b-form-select class="selborder" v-model="form.insurance_paytype" :options="paytypeOptions" />
              </v-col>
              <v-col cols="12" md="6">
                <label>{{ t('attachement', 'Attachments') }}</label>
                <input ref="createFiles" type="file" multiple accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt" @change="selectCreateFiles" />
              </v-col>
              <v-col cols="12" md="6" class="totals-box">
                <span>{{ t('subtotal', 'Subtotal') }}</span>
                <strong>{{ money(formSubtotal) }}</strong>
              </v-col>
            </v-row>
          </section>

          <div class="form-actions">
            <b-button variant="success" :disabled="saving" @click="createRental">
              <v-icon small class="mr-1">mdi-content-save-outline</v-icon>
              {{ t('save', 'Save') }}
            </b-button>
            <b-button variant="outline-secondary" @click="hide">{{ t('close', 'Close') }}</b-button>
          </div>
        </div>
      </template>
    </b-sidebar>

    <b-modal id="device_rental_view" size="xl" hide-footer :title="selectedTitle">
      <div v-if="selected" class="rental-view" :style="`direction:${lang.dir || 'ltr'}`">
        <div class="view-actions">
          <b-button size="sm" variant="primary" :disabled="selected.status != 1 || actionLoading" @click="startRental">
            <v-icon small class="mr-1">mdi-play-circle-outline</v-icon>
            {{ t('start_rent', 'Start Rent') }}
          </b-button>
          <b-button size="sm" variant="success" :disabled="selected.status != 2 || actionLoading" @click="returnRental">
            <v-icon small class="mr-1">mdi-keyboard-return</v-icon>
            {{ t('return', 'Return') }}
          </b-button>
          <b-button size="sm" variant="outline-success" :disabled="selected.invoiceid > 0 || actionLoading" @click="convertToInvoice">
            <v-icon small class="mr-1">mdi-file-document-outline</v-icon>
            {{ t('create_invoice', 'Create Invoice') }}
          </b-button>
          <b-button size="sm" variant="outline-danger" :disabled="selected.invoiceid > 0 || selected.status == 5 || actionLoading" @click="cancelRental">
            <v-icon small class="mr-1">mdi-close-circle-outline</v-icon>
            {{ t('cancel', 'Cancel') }}
          </b-button>
          <b-button size="sm" variant="danger" :disabled="actionLoading" @click="deleteRental">
            <v-icon small>mdi-delete-outline</v-icon>
          </b-button>
        </div>

        <div class="info-grid">
          <div><span>{{ t('customer', 'Customer') }}</span><strong>{{ selected.customer_name || '-' }}</strong></div>
          <div><span>{{ t('mobile', 'Mobile') }}</span><strong>{{ selected.mobile || '-' }}</strong></div>
          <div><span>{{ t('start', 'Start') }}</span><strong>{{ formatDate(selected.start_date) }}</strong></div>
          <div><span>{{ t('deadline', 'Deadline') }}</span><strong>{{ formatDate(selected.expected_return_date) }}</strong></div>
          <div><span>{{ t('actual_return', 'Actual Return') }}</span><strong>{{ formatDate(selected.actual_return_date) }}</strong></div>
          <div><span>{{ t('status', 'Status') }}</span><strong>{{ selected[`_status_${lang.langname}`] || selected._status_en }}</strong></div>
        </div>

        <section class="detail-section">
          <div class="section-title">{{ t('devices', 'Devices') }}</div>
          <v-simple-table dense>
            <thead>
              <tr>
                <th>{{ t('device', 'Device') }}</th>
                <th>{{ t('period', 'Period') }}</th>
                <th>{{ t('planned', 'Planned') }}</th>
                <th>{{ t('actual', 'Actual') }}</th>
                <th>{{ t('deadline', 'Deadline') }}</th>
                <th>{{ t('total', 'Total') }}</th>
                <th>{{ t('notes', 'Notes') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in selected.items" :key="item.id">
                <td>{{ item.device_name }}</td>
                <td>{{ periodLabel(item.period_type) }}</td>
                <td>{{ item.planned_units }} x {{ item.quantity }}</td>
                <td>{{ item.actual_units || '-' }}</td>
                <td>{{ formatDate(item.expected_return_date) }}</td>
                <td>{{ money(item.actual_total > 0 ? item.actual_total : item.planned_total) }}</td>
                <td>{{ item.item_notes || '-' }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </section>

        <section class="detail-section">
          <div class="section-row">
            <div class="section-title">{{ t('inspection', 'Inspection') }}</div>
            <b-button size="sm" variant="outline-primary" @click="addIssueRow">{{ t('add', 'Add') }}</b-button>
          </div>
          <v-simple-table dense>
            <thead>
              <tr>
                <th>{{ t('device', 'Device') }}</th>
                <th>{{ t('issue', 'Issue') }}</th>
                <th>{{ t('price', 'Price') }}</th>
                <th>{{ t('notes', 'Notes') }}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(issue, index) in inspectionRows" :key="index">
                <td><b-form-select class="selborder" v-model="issue.item_id" :options="selectedItemOptions" /></td>
                <td><b-form-input v-model="issue.issue_description" /></td>
                <td><b-form-input v-model="issue.issue_price" type="number" min="0" step="0.01" /></td>
                <td><b-form-input v-model="issue.technician_note" /></td>
                <td>
                  <b-button size="sm" variant="outline-danger" @click="inspectionRows.splice(index, 1)">
                    <v-icon small>mdi-delete-outline</v-icon>
                  </b-button>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
          <div class="section-actions">
            <b-button size="sm" variant="primary" :disabled="actionLoading" @click="saveInspection">{{ t('save', 'Save') }}</b-button>
          </div>
        </section>

        <section class="detail-section">
          <div class="section-title">{{ t('financials', 'Financials') }}</div>
          <div class="finance-grid">
            <div><span>{{ t('planned_total', 'Planned Total') }}</span><strong>{{ money(selected.planned_total) }}</strong></div>
            <div><span>{{ t('actual_total', 'Actual Total') }}</span><strong>{{ money(selected.actual_total) }}</strong></div>
            <div><span>{{ t('advance', 'Advance') }}</span><strong>{{ money(selected.paid_total) }}</strong></div>
            <div><span>{{ t('insurance_amount', 'Insurance') }}</span><strong>{{ money(selected.insurance_amount) }}</strong></div>
            <div><span>{{ t('issue_total', 'Issue Total') }}</span><strong>{{ money(selected.issue_total) }}</strong></div>
            <div><span>{{ t('deduction', 'Deduction') }}</span><strong>{{ money(selected.insurance_deduction) }}</strong></div>
            <div><span>{{ t('insurance_return', 'Insurance Return') }}</span><strong>{{ money(selected.insurance_return_amount) }}</strong></div>
            <div><span>{{ t('remaining', 'Remaining') }}</span><strong>{{ money(selected.remaining_amount) }}</strong></div>
          </div>
          <div class="payment-form">
            <b-form-select class="selborder" v-model="paymentForm.payment_kind" :options="paymentKindOptions" />
            <b-form-input class="selborder" v-model="paymentForm.amount" type="number" min="0" step="0.01" :placeholder="t('amount', 'Amount')" />
            <b-form-select class="selborder" v-model="paymentForm.paytype" :options="paytypeOptions" />
            <b-form-input class="selborder" v-model="paymentForm.notes" :placeholder="t('notes', 'Notes')" />
            <b-button size="sm" variant="success" @click="addPayment">{{ t('add_payment', 'Add Payment') }}</b-button>
          </div>
          <v-simple-table dense>
            <thead>
              <tr>
                <th>{{ t('date', 'Date') }}</th>
                <th>{{ t('type', 'Type') }}</th>
                <th>{{ t('payment_method', 'Payment Method') }}</th>
                <th>{{ t('amount', 'Amount') }}</th>
                <th>{{ t('notes', 'Notes') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="payment in selected.payments" :key="payment.id">
                <td>{{ formatDate(payment.created_date) }}</td>
                <td>{{ payment.payment_kind }} / {{ payment.direction }}</td>
                <td>{{ lang.langname === 'ar' ? (payment.type_name || payment.type_name_en) : (payment.type_name_en || payment.type_name) }}</td>
                <td>{{ money(payment.amount) }}</td>
                <td>{{ payment.notes || '-' }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </section>

        <section class="detail-section">
          <div class="section-title">{{ t('attachement', 'Attachments') }}</div>
          <div class="attachment-form">
            <input ref="detailFiles" type="file" multiple accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt" @change="selectDetailFiles" />
            <b-button size="sm" variant="primary" :disabled="!detailFiles.length || attachmentLoading" @click="uploadDetailFiles">
              <v-icon small class="mr-1">mdi-upload</v-icon>
              {{ t('upload', 'Upload') }}
            </b-button>
          </div>
          <div class="attachment-list">
            <button v-for="file in attachments" :key="file.id" class="attachment-link" @click="openAttachment(file)">
              <v-icon small>mdi-paperclip</v-icon>
              {{ file.title || file.file_name }}
            </button>
          </div>
        </section>
      </div>
    </b-modal>
  </div>
</template>

<script>
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import HeaderPortrate from '@/components/Header-Portrate.vue'
import TabsComp from '@/components/tabsComp.vue'
import { getVisibleTabsForRoute } from '@/menu/menuService'

export default {
  name: 'DeviceRentals',
  components: { HeaderPortrate, TabsComp },
  data() {
    return {
      loading: false,
      saving: false,
      actionLoading: false,
      attachmentLoading: false,
      rentals: [],
      summary: {},
      devices: [],
      paytypes: [],
      selected: null,
      attachments: [],
      createFiles: [],
      detailFiles: [],
      inspectionRows: [],
      deviceSearch: '',
      deviceSearchDone: false,
      filters: {
        status: 'current',
        search: '',
      },
      form: this.defaultForm(),
      paymentForm: {
        payment_kind: 'remaining',
        amount: '',
        paytype: 0,
        notes: '',
      },
    }
  },
  computed: {
    lang() {
      if (this.$cookies.get(this.$store.state.LangCooki) != null) {
        if (this.$cookies.get(this.$store.state.LangCooki) == 'en') return this.$store.state.lang.en
        if (this.$cookies.get(this.$store.state.LangCooki) == 'ur') return this.$store.state.lang.ur
        return this.$store.state.lang.ar
      }
      if (this.$store.state.deflang == 'en') return this.$store.state.lang.en
      if (this.$store.state.deflang == 'ur') return this.$store.state.lang.ur
      return this.$store.state.lang.ar
    },
    tabs() {
      return getVisibleTabsForRoute(this.$store.getters['state'], this.lang, this.$route.path)
    },
    statusOptions() {
      return [
        { text: this.t('current', 'Current'), value: 'current' },
        { text: this.t('new', 'New'), value: 'new' },
        { text: this.t('active', 'Active'), value: 'active' },
        { text: this.t('overdue', 'Overdue'), value: 'overdue' },
        { text: this.t('returned', 'Returned'), value: 'returned' },
        { text: this.t('invoiced', 'Invoiced'), value: 'invoiced' },
        { text: this.t('cancelled', 'Cancelled'), value: 'cancelled' },
        { text: this.t('all', 'All'), value: 'all' },
      ]
    },
    customerTypeOptions() {
      return [
        { text: this.t('individual_client', 'Individual'), value: 1 },
        { text: this.t('company_clinet', 'Company'), value: 2 },
        { text: this.t('government_clinet', 'Government'), value: 3 },
      ]
    },
    periodOptions() {
      return [
        { text: this.t('hour', 'Hour'), value: 'hour' },
        { text: this.t('day', 'Day'), value: 'day' },
        { text: this.t('week', 'Week'), value: 'week' },
        { text: this.t('month', 'Month'), value: 'month' },
      ]
    },
    paytypeOptions() {
      const items = this.paytypes.map((item) => ({
        text: this.lang.langname === 'ar' ? (item.type_name || item.type_name_en) : (item.type_name_en || item.type_name),
        value: Number(item.id),
      }))
      return [{ text: this.t('chose_from_list', 'Choose from list'), value: 0 }].concat(items)
    },
    paymentKindOptions() {
      return [
        { text: this.t('advanced_payment', 'Advanced Payment'), value: 'advance' },
        { text: this.t('rental_payment', 'Rental Payment'), value: 'remaining' },
        { text: this.t('damage_payment', 'Damage Payment'), value: 'damage' },
        { text: this.t('insurance_return', 'Insurance Return'), value: 'refund_insurance' },
      ]
    },
    deviceOptions() {
      return [{ text: this.t('manual_device', 'Manual Device'), value: 0 }].concat(this.devices.map((device) => ({
        text: this.deviceOptionLabel(device),
        value: Number(device.id),
      })))
    },
    selectedItemOptions() {
      if (!this.selected) return []
      return [{ text: this.t('general', 'General'), value: 0 }].concat((this.selected.items || []).map((item) => ({
        text: item.device_name,
        value: Number(item.id),
      })))
    },
    formSubtotal() {
      return this.form.items.reduce((total, item) => total + Number(item.planned_total || 0), 0)
    },
    selectedTitle() {
      if (!this.selected) return this.t('device_rental', 'Device Rental')
      return `${this.t('device_rental', 'Device Rental')} #${this.selected.rental_number || this.selected.id}`
    },
  },
  created() {
    this.loadPaytypes()
    this.loadRentals()
  },
  methods: {
    t(key, fallback) {
      return (this.lang && this.lang[key]) || fallback
    },
    defaultForm() {
      return {
        customer_id: 0,
        customer_type: 1,
        customer_name: '',
        mobile: '',
        id_number: '',
        company: this.defaultCompany(),
        notes: '',
        advance_amount: 0,
        advance_paytype: 0,
        insurance_amount: 0,
        insurance_paytype: 0,
        items: [this.defaultItem()],
      }
    },
    defaultCompany() {
      return {
        fullname: '',
        vatid: '',
        projectname: '',
        building_no: '',
        street_name: '',
        branch_number: '',
        district: '',
        zipcode: '',
        city: '',
        address: '',
        crt_number: '',
        entity_name: '',
        branch_name: '',
        entity_branch_number: '',
        request_number: '',
        order_number: '',
        po_number: '',
        po_date: '',
      }
    },
    defaultItem() {
      return {
        device_id: 0,
        device_code: '',
        device_name: '',
        device_details: '',
        period_type: 'day',
        unit_price: 0,
        quantity: 1,
        planned_units: 1,
        planned_total: 0,
        item_notes: '',
      }
    },
    changeRentalCustomerType(type) {
      this.form.customer_type = Number(type)
      this.syncRentalCustomerName()
    },
    syncRentalCustomerName() {
      if (Number(this.form.customer_type) === 2) {
        this.form.customer_name = this.form.company.fullname || ''
      } else if (Number(this.form.customer_type) === 3) {
        this.form.customer_name = this.form.company.entity_name || ''
      }
    },
    applyRentalCustomer(customer) {
      if (!customer || !customer.id) return
      this.form.customer_id = customer.id || customer.customerid || 0
      this.form.mobile = customer.mobile || this.form.mobile
      this.form.customer_type = Number(customer.customer_type || this.form.customer_type || 1)
      this.form.id_number = customer.id_number || this.form.id_number || ''
      this.form.customer_name = customer.full_name || this.form.customer_name || ''
      this.form.company = Object.assign(this.defaultCompany(), {
        fullname: customer.company_name || '',
        vatid: customer.company_vatid || customer.vatid || '',
        projectname: customer.projectname || '',
        building_no: customer.building_no || '',
        street_name: customer.street_name || '',
        branch_number: customer.branch_number || '',
        district: customer.district || '',
        zipcode: customer.zipcode || '',
        city: customer.city || '',
        address: customer.address || '',
        crt_number: customer.crt_number || '',
        entity_name: customer.entity_name || '',
        branch_name: customer.branch_name || '',
        entity_branch_number: customer.entity_branch_number || '',
      })
      this.syncRentalCustomerName()
    },
    async lookupRentalCustomer(mode) {
      const useId = mode === 'id'
      if ((useId && !this.form.customer_id) || (!useId && !this.form.mobile)) return
      const res = await this.postApi('customerInfo', {
        mobile: useId ? '' : this.form.mobile,
        customerid: useId ? this.form.customer_id : '',
        customer_type: this.form.customer_type,
      })
      if (!res || !res.results || !res.error || res.error.number !== 200) return
      const data = res.results.data
      const customers = Array.isArray(data) ? data : (data && data.id ? [data] : [])
      if (!customers.length) return
      const preferred = customers.find((row) => Number(row.customer_type || 1) === Number(this.form.customer_type))
      this.applyRentalCustomer(preferred || customers[0])
    },
    async postApi(type, payload = {}) {
      const post = new FormData()
      post.append('defBranch', localStorage.getItem('defBranch') || '1')
      post.append('financeYear', localStorage.getItem('financeYear') || '')
      post.append('type', type)
      post.append('auth', this.$cookies.get(this.$store.state.COOKIEPhase))
      Object.keys(payload).forEach((key) => {
        const value = payload[key]
        post.append(`data[${key}]`, typeof value === 'object' ? JSON.stringify(value) : value)
      })
      const response = await fetch(this.$store.state.SAMCOTEC.r_path, { method: 'POST', body: post })
      const body = await response.text()
      if (!body.trim()) {
        return {
          error: {
            number: response.status || 500,
            message: `${type}: empty API response`,
          },
          results: { data: [] },
        }
      }
      try {
        return JSON.parse(body)
      } catch (error) {
        return {
          error: {
            number: response.status || 500,
            message: `${type}: invalid API response`,
            details: body.substring(0, 500),
          },
          results: { data: [] },
        }
      }
    },
    async loadPaytypes() {
      const res = await this.postApi('getDeviceRentalPayTypes', { id: 'all' })
      if (res && res.error && res.error.number === 200) {
        this.paytypes = res.results.data || []
      } else if (res && res.error) {
        window.alert(res.error.message || 'Error')
      }
    },
    async loadDevices() {
      const search = String(this.deviceSearch || '').trim()
      this.devices = []
      this.deviceSearchDone = false
      if (!search) return
      const res = await this.postApi('getDeviceRentalDevices', { search })
      if (res && res.error && res.error.number === 200) {
        this.devices = res.results && Array.isArray(res.results.data) ? res.results.data : []
        this.deviceSearchDone = true
      } else if (res && res.error) {
        window.alert(res.error.message || 'Error')
      }
    },
    async loadRentals() {
      this.loading = true
      try {
        const res = await this.postApi('getDeviceRentals', this.filters)
        if (res && res.error && res.error.number === 200) {
          this.rentals = res.results.data || []
          this.summary = res.results.summary || {}
        } else if (res && res.error) {
          window.alert(res.error.message || 'Error')
        }
      } finally {
        this.loading = false
      }
    },
    setStatus(status) {
      this.filters.status = status
      this.loadRentals()
    },
    openCreate() {
      this.form = this.defaultForm()
      this.createFiles = []
      this.deviceSearch = ''
      this.devices = []
      this.deviceSearchDone = false
      if (this.$refs.createFiles) this.$refs.createFiles.value = ''
    },
    onDeviceSearchInput() {
      this.devices = []
      this.deviceSearchDone = false
    },
    addItem() {
      this.form.items.push(this.defaultItem())
    },
    removeItem(index) {
      if (this.form.items.length === 1) return
      this.form.items.splice(index, 1)
    },
    selectDevice(index) {
      const item = this.form.items[index]
      const device = this.devices.find((row) => Number(row.id) === Number(item.device_id))
      if (!device) return
      this.applyDeviceToItem(item, device)
    },
    addDeviceToRental(device) {
      let item = this.form.items.find((row) => !Number(row.device_id || 0) && !row.device_name)
      if (!item) {
        item = this.defaultItem()
        this.form.items.push(item)
      }
      this.applyDeviceToItem(item, device)
    },
    applyDeviceToItem(item, device) {
      item.device_id = Number(device.id || 0)
      item.device_code = this.deviceCode(device)
      item.device_name = this.deviceName(device)
      item.unit_price = Number(device.default_price || device.price || 0)
      this.calcItem(item)
    },
    deviceCode(device) {
      return device && (device.item_code || device.itemcode) ? (device.item_code || device.itemcode) : ''
    },
    deviceName(device) {
      if (!device) return ''
      if (this.lang.langname === 'en') {
        return device.item_nameen || device.name_en || device.name || device.item_namear || ''
      }
      return device.item_namear || device.name_ar || device.name || device.item_nameen || ''
    },
    deviceOptionLabel(device) {
      const code = this.deviceCode(device)
      return `${code} ${this.deviceName(device)}`.trim()
    },
    calcItem(item) {
      item.planned_total = this.round(Number(item.unit_price || 0) * Number(item.quantity || 0) * Number(item.planned_units || 0), 2)
    },
    selectCreateFiles(event) {
      this.createFiles = event && event.target && event.target.files ? Array.from(event.target.files) : []
    },
    async createRental() {
      this.form.items.forEach((item) => this.calcItem(item))
      this.syncRentalCustomerName()
      if (!this.form.customer_name && !this.form.mobile) {
        window.alert(this.t('customer_information', 'Customer Information'))
        return
      }
      if (!this.form.items.some((item) => item.device_name)) {
        window.alert(this.t('device_name', 'Device name'))
        return
      }
      if ((Number(this.form.advance_amount) > 0 && Number(this.form.advance_paytype) <= 0) || (Number(this.form.insurance_amount) > 0 && Number(this.form.insurance_paytype) <= 0)) {
        window.alert(this.t('payment_method', 'Payment Method'))
        return
      }
      this.saving = true
      try {
        const post = new FormData()
        post.append('defBranch', localStorage.getItem('defBranch') || '1')
        post.append('financeYear', localStorage.getItem('financeYear') || '')
        post.append('type', 'createDeviceRental')
        post.append('auth', this.$cookies.get(this.$store.state.COOKIEPhase))
        Object.keys(this.form).forEach((key) => {
          const value = this.form[key]
          post.append(`data[${key}]`, key === 'items' || key === 'company' ? JSON.stringify(value) : value)
        })
        this.createFiles.forEach((file, index) => {
          post.append(`data[files][${index}]`, file)
        })
        const response = await fetch(this.$store.state.SAMCOTEC.r_path, { method: 'POST', body: post })
        const res = await response.json()
        if (res && res.error && res.error.number === 200) {
          this.filters.status = 'current'
          this.$root.$emit('bv::toggle::collapse', 'device_rental_sidebar')
          await this.loadRentals()
        } else if (res && res.error) {
          window.alert(res.error.message || 'Error')
        }
      } finally {
        this.saving = false
      }
    },
    async viewRental(rental) {
      const res = await this.postApi('getDeviceRental', { rental_id: rental.id })
      if (res && res.error && res.error.number === 200) {
        this.selected = res.results.data
        this.inspectionRows = (this.selected.issues || []).map((issue) => ({
          item_id: Number(issue.item_id || 0),
          issue_description: issue.issue_description || '',
          issue_price: Number(issue.issue_price || 0),
          technician_note: issue.technician_note || '',
        }))
        this.paymentForm = { payment_kind: 'remaining', amount: '', paytype: 0, notes: '' }
        await this.loadAttachments()
        this.$bvModal.show('device_rental_view')
      }
    },
    async refreshSelected() {
      if (!this.selected) return
      const id = this.selected.id
      const res = await this.postApi('getDeviceRental', { rental_id: id })
      if (res && res.error && res.error.number === 200) {
        this.selected = res.results.data
      }
      await this.loadRentals()
    },
    async rentalAction(type) {
      if (!this.selected) return
      this.actionLoading = true
      try {
        const res = await this.postApi(type, { rental_id: this.selected.id })
        if (res && res.error && res.error.number === 200) {
          this.selected = res.results.data
          await this.loadRentals()
        } else if (res && res.error) {
          window.alert(res.error.message || 'Error')
        }
      } finally {
        this.actionLoading = false
      }
    },
    startRental() {
      this.rentalAction('startDeviceRental')
    },
    returnRental() {
      this.rentalAction('returnDeviceRental')
    },
    async convertToInvoice() {
      await this.rentalAction('convertDeviceRentalToInvoice')
      await this.refreshSelected()
    },
    cancelRental() {
      if (window.confirm(this.t('cancel', 'Cancel'))) this.rentalAction('cancelDeviceRental')
    },
    deleteRental() {
      if (window.confirm(this.t('delete', 'Delete'))) this.rentalAction('deleteDeviceRental')
    },
    addIssueRow() {
      this.inspectionRows.push({ item_id: 0, issue_description: '', issue_price: 0, technician_note: '' })
    },
    async saveInspection() {
      if (!this.selected) return
      this.actionLoading = true
      try {
        const res = await this.postApi('saveDeviceRentalInspection', {
          rental_id: this.selected.id,
          issues: this.inspectionRows,
        })
        if (res && res.error && res.error.number === 200) {
          this.selected = res.results.data
          await this.loadRentals()
        } else if (res && res.error) {
          window.alert(res.error.message || 'Error')
        }
      } finally {
        this.actionLoading = false
      }
    },
    async addPayment() {
      if (!this.selected || Number(this.paymentForm.amount) <= 0 || Number(this.paymentForm.paytype) <= 0) {
        window.alert(this.t('payment_method', 'Payment Method'))
        return
      }
      const res = await this.postApi('addDeviceRentalPayment', {
        rental_id: this.selected.id,
        payment_kind: this.paymentForm.payment_kind,
        amount: this.paymentForm.amount,
        paytype: this.paymentForm.paytype,
        notes: this.paymentForm.notes,
      })
      if (res && res.error && res.error.number === 200) {
        this.selected = res.results.data
        this.paymentForm = { payment_kind: 'remaining', amount: '', paytype: 0, notes: '' }
        await this.loadRentals()
      } else if (res && res.error) {
        window.alert(res.error.message || 'Error')
      }
    },
    async loadAttachments() {
      if (!this.selected) return
      const res = await this.postApi('getDeviceRentalFiles', { rental_id: this.selected.id })
      if (res && res.error && res.error.number === 200) {
        this.attachments = res.results.data || []
      }
    },
    selectDetailFiles(event) {
      this.detailFiles = event && event.target && event.target.files ? Array.from(event.target.files) : []
    },
    async uploadDetailFiles() {
      if (!this.selected || !this.detailFiles.length) return
      this.attachmentLoading = true
      try {
        const post = new FormData()
        post.append('defBranch', localStorage.getItem('defBranch') || '1')
        post.append('financeYear', localStorage.getItem('financeYear') || '')
        post.append('type', 'uploadDeviceRentalFiles')
        post.append('auth', this.$cookies.get(this.$store.state.COOKIEPhase))
        post.append('data[rental_id]', this.selected.id)
        this.detailFiles.forEach((file, index) => {
          post.append(`data[files][${index}]`, file)
        })
        const response = await fetch(this.$store.state.SAMCOTEC.r_path, { method: 'POST', body: post })
        const res = await response.json()
        if (res && res.error && res.error.number === 200) {
          this.detailFiles = []
          if (this.$refs.detailFiles) this.$refs.detailFiles.value = ''
          await this.loadAttachments()
        } else if (res && res.error) {
          window.alert(res.error.message || 'Error')
        }
      } finally {
        this.attachmentLoading = false
      }
    },
    openAttachment(file) {
      if (file && file.src) window.open(file.src, '_blank')
    },
    displayTotal(rental) {
      return Number(rental.actual_total || 0) > 0 ? rental.actual_total : rental.planned_total
    },
    periodLabel(period) {
      const found = this.periodOptions.find((item) => item.value === period)
      return found ? found.text : period
    },
    formatDate(value) {
      if (!value) return '-'
      return String(value).replace('T', ' ').substring(0, 16)
    },
    money(value) {
      const number = this.round(Number(value || 0), 2)
      if (this.$convertToComa) return this.$convertToComa(number, this.$store.state.licenseType.moneyComma)
      return number.toFixed(2)
    },
    round(value, decimals) {
      const factor = Math.pow(10, decimals || 0)
      return Math.round((Number(value || 0) + Number.EPSILON) * factor) / factor
    },
  },
}
</script>

<style scoped>
.rentals-shell {
  padding: 16px 24px 40px;
}
.rentals-toolbar,
.section-row,
.view-actions,
.payment-form,
.attachment-form,
.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.rentals-toolbar {
  justify-content: space-between;
  margin-bottom: 12px;
}
.page-title {
  margin: 0;
  font-weight: 700;
}
.muted-line {
  color: #6c757d;
  font-size: 0.85rem;
}
.filter-input {
  width: 220px;
}
.filter-select {
  width: 160px;
}
.summary-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(120px, 1fr));
  gap: 8px;
  margin-bottom: 12px;
}
.summary-cell {
  border: 1px solid #d4d8dd;
  background: #fff;
  min-height: 64px;
  padding: 10px 12px;
  text-align: start;
}
.summary-cell span {
  display: block;
  color: #5f6b76;
  font-size: 0.8rem;
}
.summary-cell strong {
  font-size: 1.4rem;
}
.summary-cell.current {
  border-color: #6f42c1;
}
.summary-cell.active {
  border-color: #0d6efd;
}
.summary-cell.overdue {
  border-color: #dc3545;
}
.summary-cell.returned {
  border-color: #198754;
}
.summary-cell.invoiced {
  border-color: #146c43;
}
.rentals-table {
  border: 1px solid #d6d6d6;
}
.rentals-table th {
  background: #111;
  color: #fff !important;
  text-align: center;
}
.rentals-table td {
  text-align: center;
  vertical-align: middle;
}
.rentals-table tr.overdue td {
  background: #fff5f5;
}
.device-chip {
  display: inline-block;
  border: 1px solid #cfd4da;
  padding: 2px 6px;
  margin: 2px;
  font-size: 0.78rem;
}
.status-pill {
  display: inline-block;
  min-width: 74px;
  padding: 3px 8px;
  font-weight: 700;
  font-size: 0.78rem;
}
.empty-state {
  text-align: center;
  padding: 24px;
  color: #6c757d;
}
.sidebar-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: #111;
  color: #fff;
}
.rental-form {
  padding: 12px;
}
.form-section,
.detail-section {
  border: 1px solid #d8dde3;
  padding: 12px;
  margin-bottom: 12px;
  background: #fff;
}
.section-title {
  font-weight: 700;
  margin-bottom: 10px;
}
.customer-type-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px;
  padding: 7px;
  margin-bottom: 12px;
  background: #111;
  color: #fff;
  font-weight: 700;
}
.customer-type-bar span {
  padding: 4px 10px;
}
.customer-type-button {
  min-width: 150px;
  border: 2px solid #fff;
  background: #f5f5f5;
  color: #111;
  padding: 5px 10px;
  font-weight: 700;
  font-size: 1rem;
}
.customer-type-button.selected {
  background: #111;
  color: #fff;
}
.customer-type-button i {
  margin-inline-start: 8px;
}
.customer-main-field {
  background: #fff700 !important;
}
.address-field {
  background: #d3ffd3 !important;
}
.device-search {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.device-results {
  margin: 10px 0 14px;
  border: 1px solid #d8dde3;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
}
.device-results-head,
.device-result-row {
  display: grid;
  grid-template-columns: 120px minmax(220px, 1fr) 110px 90px;
  gap: 10px;
  align-items: center;
  padding: 8px 10px;
}
.device-results-head {
  background: #f0f2f5;
  font-weight: 700;
  color: #20242a;
}
.device-result-row + .device-result-row {
  border-top: 1px solid #edf0f2;
}
.device-result-code {
  font-weight: 700;
}
.device-result-name {
  min-width: 0;
  overflow-wrap: anywhere;
}
.device-results-empty {
  margin: 10px 0 14px;
  padding: 10px;
  border: 1px dashed #cbd2d9;
  color: #606a76;
  background: #fafbfc;
}
.items-table th {
  background: #111;
  color: #fff !important;
}
.device-select-cell {
  min-width: 260px;
}
.device-select-cell select {
  margin-bottom: 4px;
}
@media (max-width: 720px) {
  .device-results-head {
    display: none;
  }
  .device-result-row {
    grid-template-columns: 1fr auto;
  }
  .device-result-code,
  .device-result-name {
    grid-column: 1 / -1;
  }
}
.totals-box {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 14px;
  font-size: 1rem;
}
.totals-box strong {
  font-size: 1.4rem;
}
.form-actions,
.section-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.view-actions {
  justify-content: flex-end;
  margin-bottom: 12px;
}
.info-grid,
.finance-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(160px, 1fr));
  gap: 8px;
  margin-bottom: 12px;
}
.info-grid div,
.finance-grid div {
  border: 1px solid #dde2e7;
  padding: 8px 10px;
}
.info-grid span,
.finance-grid span {
  display: block;
  color: #66717c;
  font-size: 0.78rem;
}
.payment-form {
  margin-bottom: 10px;
}
.payment-form select,
.payment-form input {
  max-width: 180px;
}
.attachment-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}
.attachment-link {
  border: 1px solid #ced4da;
  background: #fff;
  padding: 5px 8px;
  color: #0d6efd;
}
@media (max-width: 900px) {
  .summary-grid,
  .info-grid,
  .finance-grid {
    grid-template-columns: 1fr;
  }
  .rentals-shell {
    padding: 10px;
  }
  .filter-input,
  .filter-select {
    width: 100%;
  }
}
</style>
